"use client";

import type { Message } from "ably";
import { ChannelProvider, useChannel } from "ably/react";
import { useEffect, useRef } from "react";
import AblyProvider from "@/contexts/common/AblyProvider";
import { useAuthContext } from "@/contexts/common/AuthContextProvider";
import { PushType } from "@/enums/push";
import type { ProductAccessChangedEvent } from "@/types/productAccessRealtime";
import { refreshProductAccessSession } from "@/utils/api/refreshProductAccessSession";
import { getOrgChannelName } from "@/utils/common/ablyChannels";
import { LatestEventRefreshGate } from "@/utils/common/latestEventRefreshGate";
import { GetNotification } from "@/utils/common/push";
import { canUseAbly } from "@/utils/common/region";

export function shouldRefreshForProductAccessEvent(
  event: ProductAccessChangedEvent | null,
  currentUserId: string
): event is ProductAccessChangedEvent {
  return !!event?.userId && event.userId === currentUserId;
}

/**
 * Hidden subscriber: when another admin changes this user's product access,
 * soft-refresh the session so nav/gates pick up new roles without a full reload.
 */
export default function ProductAccessRealtimeSubscriber() {
  const { user } = useAuthContext();

  if (!canUseAbly() || !user?.agencyId || !user?.id) {
    return null;
  }

  const channelName = getOrgChannelName(user.agencyId);

  return (
    <AblyProvider>
      <ChannelProvider channelName={channelName}>
        <ProductAccessRealtimeSubscriberInner
          channelName={channelName}
          currentUserId={user.id}
        />
      </ChannelProvider>
    </AblyProvider>
  );
}

function ProductAccessRealtimeSubscriberInner({
  channelName,
  currentUserId,
}: Readonly<{
  channelName: string;
  currentUserId: string;
}>) {
  const { renewToken } = useAuthContext();
  const renewTokenRef = useRef(renewToken);
  const refreshGateRef = useRef(
    new LatestEventRefreshGate<ProductAccessChangedEvent>()
  );

  useEffect(() => {
    renewTokenRef.current = renewToken;
  }, [renewToken]);

  useChannel(channelName, (message: Message) => {
    const event = GetNotification<ProductAccessChangedEvent>(
      message,
      PushType.ProductAccessChanged
    );
    if (!shouldRefreshForProductAccessEvent(event, currentUserId)) return;

    // Keep the newest event if one arrives mid-refresh so roles aren't left stale.
    refreshGateRef.current.request(event, async (queued) => {
      try {
        await refreshProductAccessSession({
          shouldHave: queued.shouldHave ?? [],
          shouldNotHave: queued.shouldNotHave ?? [],
          renewToken: () => renewTokenRef.current(),
        });
      } catch (error) {
        console.error(
          "Failed to refresh product access session from Ably:",
          error
        );
      }
    });
  });

  return null;
}
